import { resolveClanTerm } from "./clanLanguage";

export const WIZARD_STEPS = ["user", "clan", "summary"];

// Sentinel value for the "+ New subclan" option in combined-mode
// signups (pick OR create). The wizard treats a selection equal to
// this string as a request to switch into the create branch; the
// submit pipeline strips it so the server only ever sees a real
// subclan id / guid (or empty when the user creates a new one).
export const SUBCLAN_NEW_SENTINEL = "__new__";

export function createDefaultPublicPages() {
  return {
    defaultPresetKey: "campaign",
    shared: {
      layout: "split",
      cardPosition: "right",
      surfaceStyle: "glass",
      contentAlignment: "left",
      backgroundType: "gradient",
      backgroundImageUrl: "",
      backgroundVideoUrl: "",
      backgroundVideoPosterUrl: "",
      backgroundGradient:
        "linear-gradient(135deg, rgba(10,18,48,0.92) 0%, rgba(31,72,126,0.88) 52%, rgba(17,148,126,0.78) 100%)",
      overlayColor: "#061224",
      overlayOpacity: 0.52,
      logoVisibility: true,
    },
    login: {
      inheritShared: true,
      layout: "",
      cardPosition: "",
      surfaceStyle: "",
      contentAlignment: "",
      backgroundType: "",
      backgroundImageUrl: "",
      backgroundVideoUrl: "",
      backgroundVideoPosterUrl: "",
      backgroundGradient: "",
      overlayColor: "",
      overlayOpacity: "",
      logoVisibility: null,
      headline: "",
      subheadline: "",
      showPageContent: true,
      showSupportCopy: true,
      showRoleSummary: true,
    },
    signup: {
      inheritShared: true,
      layout: "",
      cardPosition: "",
      surfaceStyle: "",
      contentAlignment: "",
      backgroundType: "",
      backgroundImageUrl: "",
      backgroundVideoUrl: "",
      backgroundVideoPosterUrl: "",
      backgroundGradient: "",
      overlayColor: "",
      overlayOpacity: "",
      logoVisibility: null,
      headline: "",
      subheadline: "",
      showPageContent: true,
      showSupportCopy: true,
      showRoleSummary: true,
    },
    join: {
      inheritShared: true,
      layout: "",
      cardPosition: "",
      surfaceStyle: "",
      contentAlignment: "",
      backgroundType: "",
      backgroundImageUrl: "",
      backgroundVideoUrl: "",
      backgroundVideoPosterUrl: "",
      backgroundGradient: "",
      overlayColor: "",
      overlayOpacity: "",
      logoVisibility: null,
      headline: "",
      subheadline: "",
      showPageContent: true,
      showSupportCopy: true,
      showRoleSummary: true,
    },
    content: {
      inheritShared: true,
      layout: "",
      cardPosition: "",
      surfaceStyle: "",
      contentAlignment: "",
      backgroundType: "",
      backgroundImageUrl: "",
      backgroundVideoUrl: "",
      backgroundVideoPosterUrl: "",
      backgroundGradient: "",
      overlayColor: "",
      overlayOpacity: "",
      logoVisibility: null,
      headline: "",
      subheadline: "",
      showPageContent: true,
      showSupportCopy: false,
      showRoleSummary: false,
    },
    signupWizard: {
      enabledSteps: [...WIZARD_STEPS],
      stepLabels: {
        user: "",
        clan: "",
        summary: "",
      },
      profileFieldKeys: [],
      registrationFields: [],
    },
    policy: {
      allowBackgroundImage: true,
      allowBackgroundVideo: true,
      maxImageSizeMb: 10,
      maxVideoSizeMb: 100,
      allowedImageFormats: ["jpg", "jpeg", "png", "webp"],
      allowedVideoFormats: ["mp4", "mov", "webm"],
    },
    locks: [],
    presets: [],
  };
}

export function mergePublicPages(source = {}) {
  const defaults = createDefaultPublicPages();
  const next = source && typeof source === "object" ? source : {};
  return {
    ...defaults,
    ...next,
    shared: { ...defaults.shared, ...(next.shared || {}) },
    login: { ...defaults.login, ...(next.login || {}) },
    signup: { ...defaults.signup, ...(next.signup || {}) },
    join: { ...defaults.join, ...(next.join || {}) },
    content: { ...defaults.content, ...(next.content || {}) },
    signupWizard: {
      ...defaults.signupWizard,
      ...(next.signupWizard || {}),
      stepLabels: {
        ...defaults.signupWizard.stepLabels,
        ...(next.signupWizard?.stepLabels || {}),
      },
      enabledSteps: Array.isArray(next.signupWizard?.enabledSteps)
        ? next.signupWizard.enabledSteps
        : defaults.signupWizard.enabledSteps,
      registrationFields: Array.isArray(next.signupWizard?.registrationFields)
        ? next.signupWizard.registrationFields
        : defaults.signupWizard.registrationFields,
      profileFieldKeys: Array.isArray(next.signupWizard?.profileFieldKeys)
        ? next.signupWizard.profileFieldKeys
        : defaults.signupWizard.profileFieldKeys,
    },
    policy: { ...defaults.policy, ...(next.policy || {}) },
    locks: Array.isArray(next.locks) ? next.locks : defaults.locks,
    presets: Array.isArray(next.presets) ? next.presets : defaults.presets,
  };
}

export function resolvePublicPageVariant(publicPages, pageKey) {
  const merged = mergePublicPages(publicPages);
  const shared = merged.shared || {};
  const section = merged[pageKey] || merged.content;

  if (section?.inheritShared !== false) {
    return {
      ...shared,
      ...section,
      inheritShared: true,
    };
  }

  return {
    ...shared,
    ...section,
    inheritShared: false,
  };
}

export function isPublicPagePathLocked(locks = [], path = "") {
  return Array.isArray(locks) && locks.includes(path);
}

export function setPublicPagesValue(current, path, value) {
  const clone = JSON.parse(JSON.stringify(current || {}));
  const parts = `${path || ""}`.split(".").filter(Boolean);
  if (!parts.length) return clone;
  let cursor = clone;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const key = parts[index];
    if (!cursor[key] || typeof cursor[key] !== "object") {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  cursor[parts[parts.length - 1]] = value;
  return clone;
}

export function getWizardStepLabel(publicPages, step) {
  const merged = mergePublicPages(publicPages);
  return merged.signupWizard?.stepLabels?.[step] || "";
}

export function getWizardFieldsByStep(publicPages, step) {
  const merged = mergePublicPages(publicPages);
  const fields = Array.isArray(merged.signupWizard?.registrationFields)
    ? merged.signupWizard.registrationFields
    : [];
  return fields.filter((field) => field?.step === step);
}

function normalizeStringArray(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  return values.reduce((accumulator, value) => {
    const normalized = `${value || ""}`.trim();
    if (!normalized) return accumulator;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return accumulator;
    seen.add(key);
    accumulator.push(normalized);
    return accumulator;
  }, []);
}

function buildSelectOption(label, value, extra = {}) {
  return {
    label: `${label || ""}`.trim(),
    value: `${value || ""}`.trim(),
    ...extra,
  };
}

function buildSubclanOptionValue(option = {}) {
  if (option.id) {
    return `id:${option.id}`;
  }
  if (option.guid) {
    return `guid:${option.guid}`;
  }
  return "";
}

function normalizeRoleSignupContext(roleSignupContext = {}) {
  return {
    requiresApproval: Boolean(roleSignupContext?.requiresApproval),
    createSubclanOnSignup: Boolean(roleSignupContext?.createSubclanOnSignup),
    allowExistingSubclanPick: Boolean(
      roleSignupContext?.allowExistingSubclanPick
    ),
    // The 5th admin-selectable mode. Only meaningful when both
    // allowExistingSubclanPick AND createSubclanOnSignup are true.
    // When set, the wizard renders the legacy 2-field UX (picker +
    // separate create form) where the user joins an existing subclan
    // AND creates a new one in the same signup. The default
    // combined-mode UX (single dropdown with "+ New subclan") is the
    // OR alternative when this flag is false.
    pickAndCreateSubclanOnSignup: Boolean(
      roleSignupContext?.pickAndCreateSubclanOnSignup
    ),
    // The wizard drives this value — "pick" when the user is on the
    // "Join an existing subclan" branch, "create" when they're on the
    // "Create a new subclan" branch. getSignupContextFields switches
    // which fields it emits based on this flag so only the active
    // branch's inputs participate in validation.
    subclanPickMode: `${roleSignupContext?.subclanPickMode || ''}`
      .trim()
      .toLowerCase(),
    // Clan-scoped dynamics catalog (surfaced by the public role-signup
    // endpoint, see rolesController.getPublicRoleSignupContext). Used
    // to render the subclan_dynamics chip picker when the wizard lets
    // the user create a new subclan during signup.
    dynamics: Array.isArray(roleSignupContext?.dynamics)
      ? roleSignupContext.dynamics
      : [],
    // Full list of publicly-visible tenant subclans for the picker
    // branch. Supplied by the public role-signup endpoint; when empty
    // the picker gracefully falls back to publicConfig's curated
    // featuredSubclans list so this keeps working on the non-role
    // public signup flow.
    signupSubclans: Array.isArray(roleSignupContext?.signupSubclans)
      ? roleSignupContext.signupSubclans
      : [],
    // Currently-selected subclan category (the wizard threads this
    // through whenever the user touches the "Subclan category"
    // dropdown). Used by getSignupContextFields to gate the privacy
    // selector on the category's visibilityLock setting. Empty string
    // = no category picked yet, fall back to legacy "show privacy"
    // behaviour.
    activeSubclanCategory: `${roleSignupContext?.activeSubclanCategory || ''}`
      .trim(),
  };
}

// Derive the initial subclan-pick mode from the role's assignment
// flags. Exposed for SignupWizard so we don't duplicate the
// 4-way-state decision in multiple places.
//
//   both true (pick OR create) — default to "pick"; user can toggle
//     to "create" via the "not listed" escape hatch.
//   only allowExistingSubclanPick — "pick" (no create option).
//   only createSubclanOnSignup    — "create" (no picker option).
//   neither                       — "" (no branches; auto-add subclan).
export function deriveInitialSubclanPickMode(roleSignupContext = {}) {
  const pickAllowed = Boolean(roleSignupContext?.allowExistingSubclanPick);
  const createAllowed = Boolean(roleSignupContext?.createSubclanOnSignup);
  if (pickAllowed) return "pick";
  if (createAllowed) return "create";
  return "";
}

export function getSignupContextFields(
  publicConfig = {},
  step = "clan",
  roleSignupContext = {}
) {
  if (step !== "clan") {
    return [];
  }

  const roleSignupOptions = normalizeRoleSignupContext(roleSignupContext);
  // Active branch of the subclan step. When the role permits both "pick"
  // and "create" the wizard toggles this between the two so only one
  // branch's fields are required / submitted. Empty string = no mode
  // has been set by the wizard yet; default behaviour applies.
  const activeBranch = roleSignupOptions.subclanPickMode;
  const pickerAllowed = roleSignupOptions.allowExistingSubclanPick;
  const createAllowed = roleSignupOptions.createSubclanOnSignup;
  // "Combined" mode = both pick and create are allowed AND the admin
  // hasn't opted into the simultaneous pick-AND-create UX. The wizard
  // surfaces a single dropdown with "+ New subclan" prepended to the
  // alphabetical list of existing subclans, and auto-toggles
  // subclanPickMode based on the user's selection. The legacy
  // separate picker + "create a new one" link UX is gone for this
  // mode; opting into pickAndCreate (the 5th admin mode, see
  // SUBCLAN_NEW_SENTINEL below) restores the dual-field layout for
  // signups that genuinely need both operations.
  const pickAndCreateMode = roleSignupOptions.pickAndCreateSubclanOnSignup;
  const isCombinedMode = pickerAllowed && createAllowed && !pickAndCreateMode;
  // In combined mode the picker is the canonical control regardless
  // of branch — the user's selection of "+ New subclan" is what
  // flips the wizard into create mode. Force showPicker on so the
  // emission block below renders the dropdown unconditionally.
  const showPicker =
    pickerAllowed &&
    (isCombinedMode ||
      activeBranch === "pick" ||
      activeBranch === "");
  // pickAndCreate mode (5th admin mode): the user joins an existing
  // subclan AND creates a new one in the same signup. The wizard
  // renders BOTH the picker AND the create fields together so the
  // user fills in both. The server runs both operations and adds
  // the user as a member of both subclans.
  const isPickAndCreateMode =
    pickerAllowed && createAllowed && pickAndCreateMode;
  // Create fields show when the user is on the "create" branch, OR when
  // the role is create-only (no picker exposed) — the latter preserves
  // the pre-branching behaviour for roles that haven't flipped on
  // allowExistingSubclanPick. In combined mode the create fields appear
  // alongside the dropdown when the user chooses "+ New subclan". In
  // pickAndCreate (AND) mode they always render alongside the picker.
  const showCreateFields =
    createAllowed &&
    (isPickAndCreateMode ||
      activeBranch === "create" ||
      (!pickerAllowed && activeBranch === ""));

  // Resolve clan-language overrides up-front so every label and
  // description below reads in the clan's own vocabulary. The "Title"
  // variants are used at the start of labels (e.g. "Subclan name" →
  // "Organization name") while the "lower" variants are used in the
  // middle of helper-text sentences (e.g. "this subclan will work on"
  // → "this organization will work on"). For "lower" we DON'T fall
  // through to the canonical seed default — that would leak the
  // capitalised "Subclan" into mid-sentence — instead we peek at the
  // raw override and fall back to a hand-written lowercase string.
  const clanLanguage =
    publicConfig?.clanLanguage && typeof publicConfig.clanLanguage === "object"
      ? publicConfig.clanLanguage
      : {};
  const peekOverride = (key, fallback) => {
    const raw = `${clanLanguage?.[key] || ""}`.trim();
    return raw || fallback;
  };
  const subclanT         = resolveClanTerm(clanLanguage, "subclan", "Subclan");
  const subclanLow       = peekOverride("subclan", "subclan");
  const subclanCategoryT = resolveClanTerm(clanLanguage, "subclan_category", "Subclan category");
  const subclanCategoryLow = peekOverride("subclan_category", "subclan category");
  const dynamicsLow      = peekOverride("dynamics", "dynamics");
  const clanLow          = peekOverride("clan", "clan");
  // "category" here is the user's Profiles category (Owner /
  // Supervisor / Volunteer in EPWP) — distinct from subclan_category
  // (the Partnership / etc. grouping). Seeded under the Profiles tab
  // in clanLanguage.js so admins can rename "Category" → "Capacity"
  // / "Designation" / etc.
  const categoryT       = resolveClanTerm(clanLanguage, "category", "Category");
  const categoryLow     = peekOverride("category", "category");

  const fields = [];
  const categories = normalizeStringArray(publicConfig?.categories);
  const subclanCategories = Array.isArray(publicConfig?.subclanCategories)
    ? publicConfig.subclanCategories
        .map((entry) => ({
          name: `${entry?.name || ""}`.trim(),
          hasDefaultSubclan: Boolean(entry?.defaultSubclanId || entry?.defaultSubclanGuid),
          // Per-category visibility lock — see adminConfigModel.js
          // subclanCategorySchema.visibilityLock. 'owner' = legacy
          // owner-picks behaviour; 'public' / 'private' force every
          // subclan in the category to that mode and tell the wizard
          // to hide the privacy field for the locked category.
          visibilityLock: `${entry?.visibilityLock || "owner"}`
            .trim()
            .toLowerCase(),
        }))
        .filter((entry) => entry.name)
    : [];
  // The wizard tracks the user's current category pick on
  // roleSignupOptions.activeSubclanCategory so we can hide the privacy
  // selector only when the chosen category is locked. Empty / unknown
  // category names fall through to the legacy "show privacy" path.
  const activeSubclanCategoryName = `${
    roleSignupOptions.activeSubclanCategory || ""
  }`
    .trim()
    .toLowerCase();
  const activeSubclanCategoryEntry = activeSubclanCategoryName
    ? subclanCategories.find(
        (entry) => entry.name.toLowerCase() === activeSubclanCategoryName
      ) || null
    : null;
  const activeCategoryVisibilityLock =
    activeSubclanCategoryEntry?.visibilityLock || "owner";
  const activeCategoryLocksVisibility = [
    "public",
    "private",
    "protected",
    "secret",
  ].includes(activeCategoryVisibilityLock);
  // Prefer the full role-signup list (from
  // rolesController.getPublicRoleSignupContext); fall back to the
  // tenant's curated featuredSubclans for non-role public flows.
  const fullSignupSubclans = Array.isArray(roleSignupOptions.signupSubclans)
    ? roleSignupOptions.signupSubclans
        .map((entry) => ({
          id: `${entry?.id || ""}`.trim(),
          guid: Number(entry?.guid) || 0,
          label: `${entry?.name || entry?.label || ""}`.trim(),
          categoryName: `${entry?.categoryName || ""}`.trim(),
          source: "tenant",
        }))
        .filter((entry) => entry.id || entry.guid)
    : [];
  const featuredSubclans = Array.isArray(publicConfig?.subclans?.featuredSubclans)
    ? publicConfig.subclans.featuredSubclans
        .map((entry) => ({
          id: `${entry?.id || ""}`.trim(),
          guid: Number(entry?.guid) || 0,
          label: `${entry?.label || ""}`.trim(),
          categoryName: `${entry?.categoryName || ""}`.trim(),
          source: `${entry?.source || ""}`.trim(),
        }))
        .filter((entry) => entry.id || entry.guid)
    : [];
  // The selector is populated from the full list if the role endpoint
  // supplied it; otherwise the legacy featuredSubclans curated set.
  const pickerSubclans = fullSignupSubclans.length
    ? fullSignupSubclans
    : featuredSubclans;

  // Every field below carries a `fieldTypeKey` that resolves into
  // PlatformSettings.fieldTypes. That key is the catalog's source of
  // truth for baseType / validation / icon / description. The `type`
  // + `options` / `resolvedOptions` properties stay on each field for
  // now so the current SignupWizard renderer (which reads `type`
  // directly) keeps working unchanged — the renderer swap to
  // CustomFieldInput happens in Stage C. Once that lands, consumers
  // will read baseType via the catalog lookup and options via
  // `resolvedOptions`, and the raw `type` key can be removed.
  if (categories.length) {
    const restrictsCreatorCategory = Array.isArray(
      publicConfig?.subclans?.allowedCreatorUserCategories
    )
      ? publicConfig.subclans.allowedCreatorUserCategories.length > 0
      : false;
    const categoryOptions = categories.map((category) =>
      buildSelectOption(category, category)
    );
    fields.push({
      key: "category",
      fieldTypeKey: "clan_user_category", // tenant-dynamic options
      label: `Your ${categoryT}`,
      type: "select",
      required: true,
      step: "clan",
      options: categoryOptions,
      resolvedOptions: categoryOptions,
      description: restrictsCreatorCategory
        ? `Choose the ${categoryLow} that identifies you inside the ${clanLow}. This also controls which ${subclanLow} tools you can create later.`
        : `Choose the ${categoryLow} that best describes how you fit into this ${clanLow}.`,
    });
  }

  // Subclan category only matters when the wizard is on the *create*
  // branch — joining an existing subclan inherits the category from
  // the picked subclan server-side, so asking for it again was
  // redundant noise that surfaced as a near-empty card on every
  // role-based signup. Two cases keep the field:
  //
  //   1. createSubclanOnSignup with the create branch active — we
  //      genuinely need to know which category the new subclan
  //      belongs to.
  //   2. The legacy non-role public signup flow (no
  //      allowExistingSubclanPick / createSubclanOnSignup at all)
  //      where the subclan category drives default-subclan
  //      placement (see authController.resolveRegistrationSignupContext
  //      → categoryShortcut).
  //
  // Additional gate: when only ONE subclan category is signup-eligible
  // (publicConfig.subclanCategories already filters out allowSignup=false
  // entries server-side) there's no real choice for the user to make,
  // and the "select the only card" UX is dead weight. Drop the field
  // entirely; getSignupContextDefaults seeds the lone category into
  // form state so the server still receives a populated value on
  // submission.
  const isRoleBasedSignupFlow = pickerAllowed || createAllowed;
  const includeSubclanCategoryField =
    subclanCategories.length > 1 &&
    (showCreateFields || !isRoleBasedSignupFlow);
  if (includeSubclanCategoryField) {
    const subclanCategoryOptions = subclanCategories.map((entry) =>
      buildSelectOption(entry.name, entry.name, {
        hasDefaultSubclan: entry.hasDefaultSubclan,
      })
    );
    fields.push({
      key: "signupSubclanCategory",
      fieldTypeKey: "subclan_category", // tenant-dynamic options
      label: subclanCategoryT,
      type: "select",
      required: true,
      step: "clan",
      options: subclanCategoryOptions,
      resolvedOptions: subclanCategoryOptions,
      description: showCreateFields
        ? `Tell us which ${subclanCategoryLow} your new ${subclanLow} should belong to.`
        : `Tell us which ${subclanCategoryLow} should shape your onboarding path and default placement.`,
    });
  }

  // "Pick an existing subclan" branch. Emit the selector when:
  //   - the wizard is on the "pick" branch, OR
  //   - no mode has been set and the role allows picking (so pick is
  //     the initial branch), OR
  //   - the role does NOT expose either branch (neither pick nor
  //     create) but there is still a curated featuredSubclans list —
  //     this keeps the legacy optional "Starting subclan" dropdown on
  //     the platform-wide public signup flow that doesn't go through
  //     the role endpoint.
  const showLegacyOptionalPicker =
    !pickerAllowed && !createAllowed && featuredSubclans.length > 0;
  if (
    (showPicker && pickerSubclans.length) ||
    showLegacyOptionalPicker ||
    (isCombinedMode && pickerSubclans.length === 0)
  ) {
    const rawSource = showPicker ? pickerSubclans : featuredSubclans;
    // Combined mode wants the existing subclans alphabetical so the
    // "+ New subclan" option sits visibly on top and the rest reads
    // like a phone-book. The non-combined branches preserve the
    // server-supplied order (which is name-sorted for the role-signup
    // endpoint anyway, but we don't want to assume).
    const sortedSource = isCombinedMode
      ? [...rawSource].sort((a, b) =>
          `${a.label || ""}`.localeCompare(`${b.label || ""}`, undefined, {
            sensitivity: "base",
          })
        )
      : rawSource;
    const subclanOptions = sortedSource
      .map((entry) =>
        buildSelectOption(
          entry.categoryName ? `${entry.label} (${entry.categoryName})` : entry.label,
          buildSubclanOptionValue(entry),
          {
            categoryName: entry.categoryName,
            source: entry.source,
          }
        )
      )
      .filter((entry) => entry.value);
    // In combined mode the "+ New subclan" sentinel is the first
    // option. The wizard auto-flips subclanPickMode to "create" when
    // it's selected (driving the create-fields emission below) and
    // back to "pick" when the user picks a real subclan. The sentinel
    // value is dropped from the submitted payload by buildSubmitPayload
    // in pick mode → "" branch, so the server never sees it.
    const newSubclanOptionLabel = `+ New ${subclanLow}`;
    const pickerOptions = isCombinedMode
      ? [
          buildSelectOption(newSubclanOptionLabel, SUBCLAN_NEW_SENTINEL, {
            isCreateNew: true,
          }),
          ...subclanOptions,
        ]
      : subclanOptions;
    fields.push({
      key: "signupSubclanSelection",
      // reference_subclan is the catalog entry that maps reference
      // picks onto an existing subclan id. In the role-signup flow
      // the options are the FULL list of publicly-visible tenant
      // subclans (see rolesController.resolveSignupVisibleSubclans);
      // the legacy fallback uses publicConfig's curated featuredSubclans.
      fieldTypeKey: "reference_subclan",
      label: isCombinedMode
        ? subclanT
        : showPicker
        ? `Join an existing ${subclanLow}`
        : `Starting ${subclanLow}`,
      type: "select",
      // When the wizard is driving the "pick" branch (or the
      // combined dropdown — its sentinel is required too), the
      // selection is required so the user commits to a choice
      // before leaving the step. Otherwise (legacy optional picker)
      // it stays optional.
      required: Boolean(showPicker || isCombinedMode),
      step: "clan",
      options: pickerOptions,
      resolvedOptions: pickerOptions,
      description: isCombinedMode
        ? `Pick an existing ${subclanLow} from the list, or choose "${newSubclanOptionLabel}" to create one.`
        : showPicker
        ? `Select the ${subclanLow} you're joining. Can't find it? Use the option below to create a new one.`
        : `Optional: start inside a featured ${subclanLow} immediately if one has already been configured for this ${clanLow}.`,
    });
  }

  if (showCreateFields) {
    fields.push(
      {
        key: "signupSubclanName",
        fieldTypeKey: "subclan_name",
        label: `${subclanT} name`,
        type: "text",
        required: true,
        step: "clan",
        description:
          `Choose the name for the ${subclanLow} that should be created from this signup.`,
      },
      {
        key: "signupSubclanDescription",
        fieldTypeKey: "subclan_description",
        label: `${subclanT} description`,
        type: "textarea",
        required: false,
        step: "clan",
        description:
          `Optional: describe the focus or purpose of this ${subclanLow} for reviewers and future members.`,
      }
    );

    // Privacy selector — emitted only when the chosen subclan
    // category is on 'owner' visibility (the legacy owner-picks
    // mode). When the admin has locked the category to 'public' or
    // 'private', every subclan in that category is forced to the
    // locked mode server-side, so showing the picker would just be
    // confusing UX. Hiding the field also drops it from
    // validation / submission so the wizard's "Continue" button
    // doesn't block on a missing value the user can't supply.
    if (!activeCategoryLocksVisibility) {
      fields.push({
        key: "signupSubclanPrivacy",
        fieldTypeKey: "subclan_privacy",
        label: `${subclanT} privacy`,
        type: "select",
        required: true,
        step: "clan",
        // Catalog entry owns the canonical [public, private] option
        // list; we copy it here so the current (pre-Stage-C) renderer
        // keeps working without looking the catalog up.
        options: [
          buildSelectOption("Public", "public"),
          buildSelectOption("Private", "private"),
        ],
        resolvedOptions: [
          buildSelectOption("Public", "public"),
          buildSelectOption("Private", "private"),
        ],
        description:
          `Choose whether the new ${subclanLow} should be open to the ${clanLow} or reviewed before access is granted.`,
      });
    }

    // Dynamics chip picker — only shown when the clan has at least one
    // non-archived dynamic defined. Below the privacy field because
    // dynamics are finer-grained than the name/description/privacy
    // basics. The catalog entry's baseType is multi_reference with
    // referenceTarget=dynamic; resolvedOptions carries the clan's
    // dynamics catalog through to the picker.
    if (roleSignupOptions.dynamics.length > 0) {
      fields.push({
        key: "signupSubclanDynamics",
        fieldTypeKey: "subclan_dynamics",
        label: `${subclanT} ${dynamicsLow}`,
        type: "multi_reference",
        required: false,
        step: "clan",
        resolvedOptions: roleSignupOptions.dynamics,
        description:
          `Pick one or more ${dynamicsLow} that this ${subclanLow} will work on. These come from the ${clanLow}'s configured ${dynamicsLow} catalog.`,
      });
    }
  }

  return fields;
}

export function getSignupContextDefaults(publicConfig = {}, roleSignupContext = {}) {
  const clanFields = getSignupContextFields(publicConfig, "clan", roleSignupContext);
  const accumulator = clanFields.reduce((acc, field) => {
    const options = Array.isArray(field.options) ? field.options : [];
    if (options.length === 1) {
      acc[field.key] = options[0].value;
    }
    if (field.key === "signupSubclanPrivacy" && !acc[field.key]) {
      acc[field.key] = "public";
    }
    return acc;
  }, {});

  // When getSignupContextFields drops the subclan-category picker
  // because only one category is signup-eligible, still seed the
  // form so the server receives the resolved name on submission.
  // Mirrors the field-level "single option" default-seeding above
  // but for the field that no longer makes it into clanFields.
  const onlyEligibleSubclanCategory =
    getOnlySignupEligibleSubclanCategory(publicConfig);
  if (onlyEligibleSubclanCategory && !accumulator.signupSubclanCategory) {
    accumulator.signupSubclanCategory = onlyEligibleSubclanCategory;
  }

  return accumulator;
}

export function getOnlySignupEligibleSubclanCategory(publicConfig = {}) {
  const eligibleSubclanCategories = Array.isArray(publicConfig?.subclanCategories)
    ? publicConfig.subclanCategories
        .map((entry) => {
          if (typeof entry === "string") return entry.trim();
          if (entry?.allowSignup === false) return "";
          return `${entry?.name || ""}`.trim();
        })
        .filter(Boolean)
    : [];

  return eligibleSubclanCategories.length === 1
    ? eligibleSubclanCategories[0]
    : "";
}

// Lookup helper for the wizard to know whether the currently-selected
// subclan category locks visibility (and to what value). Mirrors the
// gating logic in getSignupContextFields so the wizard can pre-fill
// signupSubclanPrivacy with the locked value before submit — keeping
// the request payload consistent with what the server enforces.
export function getSubclanCategoryVisibilityLock(
  publicConfig = {},
  categoryName = ""
) {
  const trimmed = `${categoryName || ""}`.trim().toLowerCase();
  if (!trimmed) return "owner";
  const categories = Array.isArray(publicConfig?.subclanCategories)
    ? publicConfig.subclanCategories
    : [];
  const matched = categories.find(
    (entry) => `${entry?.name || ""}`.trim().toLowerCase() === trimmed
  );
  const lock = `${matched?.visibilityLock || "owner"}`.trim().toLowerCase();
  return ["public", "private", "protected", "secret"].includes(lock)
    ? lock
    : "owner";
}
